<template>
  <header v-if="!Object.values(isActive).some((x) => x)">
    <slot name="append" />
  </header>

  <template v-for="name in alertOrder.filter((x) => !builtInAlerts.includes(x))">
    <header v-if="isActive[name]">
      <slot :name="name" />
      <slot name="append" />
    </header>
  </template>

  <header v-if="isActive['unlicensed']">
    <div class="bg-primary px-4 py-4">
      <i18n-t class="text-sm [&_p]:leading-relaxed" keypath="err.unlicensedDescription" tag="p">
        <template v-slot:url>
          <a href="https://licensing.knocknoc.io" class="underline hover:no-underline" target="_blank">
            licensing.knocknoc.io
          </a>
        </template>
      </i18n-t>
    </div>

    <slot name="append" />
  </header>
  <header v-else-if="isActive['agentIncompatible']">
    <div class="flex w-full flex-col gap-3 bg-primary px-4 py-3 text-primary-foreground sm:flex-row sm:items-center">
      <div class="flex items-center gap-3">
        <div class="flex-shrink-0">
          <ExclamationTriangleIcon class="h-8 w-8" />
        </div>

        <div class="min-w-0 flex-1">
          <div class="text-sm font-semibold">
            {{ t("pages.header.anIncompatibleAgentVersion") }}.
            {{ t("pages.header.agentVersionRequired", { version: minRequiredAgentVersion.version }) }}.
          </div>
          <router-link to="/admin/agents" class="mt-1 flex w-fit items-center gap-1 text-xs text-primary-foreground/75">
            {{ t("msg.clickHereToSeeAgents") }}
          </router-link>
        </div>

        <button
          class="inline-flex flex-shrink-0 items-center justify-center rounded-md p-1 text-primary-foreground/80 transition-colors hover:bg-primary-foreground/10 hover:text-primary-foreground focus:outline-none focus:ring-2 focus:ring-primary-foreground/50 sm:hidden"
          type="button"
          :aria-label="t('common.close')"
          @click="adminStore.dismissIncompatibleAgentAlert()"
        >
          <XMarkIcon class="h-5 w-5" />
        </button>
      </div>

      <div class="flex items-center gap-2 sm:ml-auto sm:flex-shrink-0">
        <button
          class="hidden flex-shrink-0 items-center justify-center rounded-md p-1 text-primary-foreground/80 transition-colors hover:bg-primary-foreground/10 hover:text-primary-foreground focus:outline-none focus:ring-2 focus:ring-primary-foreground/50 sm:inline-flex"
          type="button"
          :aria-label="t('common.close')"
          @click="adminStore.dismissIncompatibleAgentAlert()"
        >
          <XMarkIcon class="h-5 w-5" />
        </button>
      </div>
    </div>
    <slot name="append" />
  </header>
  <header v-else-if="isActive['userLimitExceeded']">
    <div class="relative bg-destructive px-4 py-6 text-destructive-foreground">
      <button
        class="absolute right-2 top-2 rounded p-1 hover:bg-destructive-foreground/10"
        type="button"
        :aria-label="t('common.close')"
        @click="adminStore.dismissUserLimitExceeded()"
      >
        <XMarkIcon class="h-5 w-5" />
      </button>

      <div class="mx-auto max-w-7xl">
        <div class="flex flex-col gap-4 lg:flex-row lg:items-start lg:gap-6">
          <div class="flex items-start gap-4">
            <div class="flex-shrink-0">
              <ExclamationTriangleIcon class="h-10 w-10 text-destructive-foreground" />
            </div>
            <div class="flex-1">
              <h2 class="mb-2 text-lg font-bold">User License Limit Exceeded</h2>
              <div class="space-y-2 text-sm">
                <p class="font-medium">
                  {{ t("pages.header.userCountExceedingLimit", { userCount, userLimit }) }}
                </p>
                <p v-if="userOverage > 0 && userCount < userLimit + userOverage">
                  <span class="font-medium text-yellow-200">Grace Period:</span>
                  {{ t("pages.header.additionalUsersAllowed", { userOverage, userOveragePercentage }) }}
                </p>
                <p v-if="userCount > userLimit + userOverage" class="text-yellow-200">
                  {{ t("pages.header.accessDeniedUntilLicenseUpgraded") }}
                </p>
                <p v-else-if="userOverage > 0" class="text-yellow-200">
                  <template v-if="userCount - userLimit > 1">
                    {{ t("pages.header.usersOverLimit", { over: userCount - userLimit }) }}
                  </template>
                  <template v-else>
                    {{ t("pages.header.userOverLimit") }}
                  </template>
                  {{ t("pages.header.serviceDisabledIfExceededUsers", { limit: userLimit + userOverage }) }}
                </p>
              </div>
            </div>
          </div>

          <!-- License Usage Visualization -->
          <div class="lg:w-96 lg:flex-shrink-0">
            <div class="rounded-lg bg-destructive-foreground/10 p-3">
              <h3 class="mb-2 text-sm font-semibold text-destructive-foreground">License Usage</h3>
              <div class="mb-2">
                <div class="mb-1 flex justify-between text-xs text-destructive-foreground/90">
                  <span>0</span>
                  <span>{{ Math.max(userLimit + (userOverage || 0), userCount) }}</span>
                </div>
                <div class="h-2 w-full rounded-full bg-destructive-foreground/20">
                  <div class="relative h-full overflow-hidden rounded-full">
                    <div
                      class="absolute left-0 top-0 h-full bg-red-400 transition-all duration-300"
                      :style="{
                        width: `${Math.min((userLimit / Math.max(userCount, userLimit + (userOverage || 0))) * 100, 100)}%`,
                      }"
                    ></div>
                    <div
                      v-if="userOverage > 0"
                      class="absolute top-0 h-full bg-yellow-400 transition-all duration-300"
                      :style="{
                        left: `${(userLimit / Math.max(userCount, userLimit + userOverage)) * 100}%`,
                        width: `${Math.min((userOverage / Math.max(userCount, userLimit + userOverage)) * 100, 100 - (userLimit / Math.max(userCount, userLimit + userOverage)) * 100)}%`,
                      }"
                    ></div>
                    <div
                      class="absolute left-0 top-0 h-full bg-white/30 transition-all duration-300"
                      :style="{
                        width: `${Math.min((userCount / Math.max(userCount, userLimit + (userOverage || 0))) * 100, 100)}%`,
                      }"
                    ></div>
                  </div>
                </div>
              </div>

              <!-- Usage Stats -->
              <div class="grid grid-cols-2 gap-1.5 text-xs">
                <div class="rounded bg-destructive-foreground/10 p-1.5">
                  <div class="text-xs font-medium text-destructive-foreground">Licensed users</div>
                  <div class="text-sm font-bold text-red-200">{{ userLimit }}</div>
                </div>
                <div v-if="userOverage > 0" class="rounded bg-destructive-foreground/10 p-1.5">
                  <div class="text-xs font-medium text-destructive-foreground">Grace</div>
                  <div class="text-sm font-bold text-yellow-200">+{{ userOverage }}</div>
                </div>
                <div class="rounded bg-destructive-foreground/10 p-1.5" :class="{ 'col-span-2': !userOverage }">
                  <div class="text-xs font-medium text-destructive-foreground">Current</div>
                  <div
                    class="text-sm font-bold"
                    :class="userCount > userLimit + (userOverage || 0) ? 'text-red-200' : 'text-yellow-200'"
                  >
                    {{ userCount }}
                  </div>
                </div>
                <div v-if="userOverage > 0" class="rounded bg-destructive-foreground/10 p-1.5">
                  <div class="text-xs font-medium text-destructive-foreground">Remaining</div>
                  <div class="text-sm font-bold text-destructive-foreground">
                    {{ userLimit + userOverage - userCount }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="flex flex-wrap gap-3 max-lg:mt-6">
          <a
            href="https://licensing.knocknoc.io"
            target="_blank"
            rel="noopener noreferrer"
            class="inline-flex items-center gap-2 rounded-md bg-destructive-foreground px-4 py-2 text-sm font-medium text-destructive no-underline transition-colors hover:bg-destructive-foreground/90 focus:outline-none focus:ring-2 focus:ring-destructive-foreground/50"
          >
            <DocumentTextIcon class="h-4 w-4" />
            Upgrade License
          </a>
          <router-link
            to="/admin/users"
            class="inline-flex items-center gap-2 rounded-md border border-destructive-foreground/30 px-4 py-2 text-sm font-medium text-destructive-foreground no-underline transition-colors hover:bg-destructive-foreground/10 focus:outline-none focus:ring-2 focus:ring-destructive-foreground/50"
          >
            Manage Users
          </router-link>
        </div>
      </div>
    </div>
    <slot name="append" />
  </header>
  <header v-else-if="isActive['serverUpdate']">
    <div class="flex w-full flex-col gap-3 bg-primary px-4 py-3 text-primary-foreground sm:flex-row sm:items-center">
      <div class="flex items-center gap-3">
        <Tooltip
          aria-label="Warning urgency"
          :focusable="true"
          :text="mostUrgentServerVersion?.urgency ? `${mostUrgentServerVersion?.urgency} urgency` : ''"
        >
          <div class="flex-shrink-0">
            <ExclamationTriangleIcon class="h-8 w-8" />
          </div>
        </Tooltip>

        <div class="min-w-0 flex-1">
          <div class="text-sm font-semibold">
            {{ t(`err.serverOutdated${mostUrgentServerVersion?.urgency || "default"}`) }}
          </div>
          <span
            v-if="
              mostUrgentServerVersion?.description || (isImportantChange && mostUrgentServerVersion?.changelog_link)
            "
            class="mt-1 w-fit gap-1 text-xs text-primary-foreground/75"
          >
            <template v-if="mostUrgentServerVersion?.description">
              {{ mostUrgentServerVersion?.description }}
              <template v-if="isImportantChange && mostUrgentServerVersion?.changelog_link"> - </template>
            </template>

            <a
              v-if="isImportantChange && mostUrgentServerVersion?.changelog_link"
              :href="`https://docs.knocknoc.io/${mostUrgentServerVersion?.changelog_link}`"
              target="_blank"
              class="underline"
            >
              {{ t("msg.clickToSeeChanges") }}
            </a>
          </span>
        </div>

        <button
          class="inline-flex flex-shrink-0 items-center justify-center rounded-md p-1 text-primary-foreground/80 transition-colors hover:bg-primary-foreground/10 hover:text-primary-foreground focus:outline-none focus:ring-2 focus:ring-primary-foreground/50 sm:hidden"
          type="button"
          :aria-label="t('common.close')"
          @click="adminStore.dismissServerUpdate()"
        >
          <XMarkIcon class="h-5 w-5" />
        </button>
      </div>

      <div class="flex items-center gap-2 sm:ml-auto sm:flex-shrink-0">
        <a
          v-if="latestChangeLogsUrl"
          :href="latestChangeLogsUrl"
          target="_blank"
          rel="noopener noreferrer"
          class="inline-flex flex-1 items-center justify-center gap-1 rounded-md border border-primary-foreground/30 px-2 py-3 text-sm text-primary-foreground no-underline transition-colors hover:bg-primary-foreground/10 hover:text-primary-foreground focus:outline-none focus:ring-2 focus:ring-primary-foreground/50 sm:flex-initial"
        >
          <DocumentTextIcon class="h-4 w-4" />
          See the latest change logs
        </a>

        <button
          class="hidden flex-shrink-0 items-center justify-center rounded-md p-1 text-primary-foreground/80 transition-colors hover:bg-primary-foreground/10 hover:text-primary-foreground focus:outline-none focus:ring-2 focus:ring-primary-foreground/50 sm:inline-flex"
          type="button"
          :aria-label="t('common.close')"
          @click="adminStore.dismissServerUpdate()"
        >
          <XMarkIcon class="h-5 w-5" />
        </button>
      </div>
    </div>
    <slot name="append" />
  </header>
</template>

<script setup>
import { DocumentTextIcon, ExclamationTriangleIcon, XMarkIcon } from "@heroicons/vue/24/outline";
import { computed, onMounted, onUnmounted } from "vue";
import { useI18n } from "vue-i18n";
import Tooltip from "./ui/Tooltip.vue";
import { useAdminStore } from "../pinia/admin.ts";
import { useUserStore } from "../pinia/user.ts";
import { parseKnocknocVersion } from "../utils/versions";

const builtInAlerts = ["unlicensed", "agentIncompatible", "userLimitExceeded", "serverUpdate"];

const props = defineProps({
  alertTriggers: {
    type: Object,
    default: () => {},
    validator: (v) => !v || Object.values(v).every((x) => typeof x === "function"),
  },
  alertOrder: {
    type: Array,
    default: () => ["unlicensed", "agentIncompatible", "userLimitExceeded", "serverUpdate"],
    validator: (v) =>
      ["unlicensed", "agentIncompatible", "userLimitExceeded", "serverUpdate"].every((x) => v.includes(x)),
  },
});

const alertConditions = {
  unlicensed: () => isLicensed.value === false,
  agentIncompatible: () => !cloudMode.value && agentIncompatible.value && !incompatibleAgentDismissed.value,
  userLimitExceeded: () => userCount.value > userLimit.value && hasSamlUsers.value && !userLimitExceededDismissed.value,
  serverUpdate: () => !cloudMode.value && mostUrgentServerVersion.value && !serverUpdateDismissed.value,
};

function conditionActive(name) {
  for (const x of props.alertOrder) {
    if (x === name) {
      if (alertConditions?.[x]?.()) {
        return true;
      } else if (props.alertTriggers?.[x]?.()) {
        return true;
      }
      return false;
    }

    if (alertConditions?.[x]?.()) {
      return false;
    }
    if (props.alertTriggers?.[x]?.()) {
      return false;
    }
  }
}

const isActive = computed(() =>
  props.alertOrder.reduce((acc, x) => {
    acc[x] = conditionActive(x);
    return acc;
  }, {}),
);

const adminStore = useAdminStore();
const userStore = useUserStore();

const { t } = useI18n();

const isLicensed = computed(() => adminStore.user?.Licensing?.Licensed);
const serverUpdateDismissed = computed(() => adminStore.serverUpdateDismissed);
const incompatibleAgentDismissed = computed(() => adminStore.incompatibleAgentAlertDismissed);
const userLimitExceededDismissed = computed(() => adminStore.userLimitExceededDismissed);

const cloudMode = computed(() => adminStore.user?.Cloud);
const serverVersion = computed(() => adminStore.user?.ServerVersion);
const availableServerVersions = computed(() => adminStore.user?.AvailableServerVersions);
const latestChangeLogsUrl = computed(() => adminStore.user?.LatestChangeLogsUrl);

const minRequiredAgentVersion = computed(() => adminStore.user?.MinimumRequiredAgentVersion);
const agents = computed(() => userStore.agents?.data);

const userCount = computed(() => adminStore.user?.UserCount);
const hasSamlUsers = computed(() => userStore.users?.data?.some((user) => user.AuthType === "SAML"));
const userLimit = computed(() => adminStore.user?.Licensing?.UserLimit);
const userOverage = computed(() => adminStore.user?.Licensing?.UserLimitOverage);
const userOveragePercentage = computed(() => adminStore.user?.Licensing?.UserLimitOveragePercentage);

const mostUrgentServerVersion = computed(() => {
  if (!serverVersion.value || serverVersion.value === "LOCAL BUILD" || !availableServerVersions.value) {
    return;
  }

  let releventVersions = null;
  for (const urgency of ["critical", "high", "medium", "low"]) {
    const versions = availableServerVersions.value.filter((x) => x.urgency === urgency);
    if (versions.length > 0) {
      releventVersions = versions;
      break;
    }
  }

  if (!releventVersions) {
    // This only happens if urgency is null / "" for all versions
    releventVersions = availableServerVersions.value;
  }

  return releventVersions.reduce((acc, x, i) => {
    if (i !== 0 && parseKnocknocVersion(x.version).gtes(acc.version)) {
      return x;
    }
    return acc;
  }, releventVersions[0]);
});

const agentIncompatible = computed(() => {
  return (
    agents.value &&
    minRequiredAgentVersion.value &&
    agents.value.some((agent) => {
      return agent.Version && parseKnocknocVersion(minRequiredAgentVersion.value.version).gts(agent.Version);
    })
  );
});

const isImportantChange = computed(
  () => mostUrgentServerVersion.value?.urgency === "high" || mostUrgentServerVersion.value?.urgency === "critical",
);

let agentInterval;
let adminInterval;

onMounted(() => {
  userStore.fetchAgents();
  adminStore.fetchStatus();
  agentInterval = setInterval(async () => userStore.fetchAgents(), 3 * 60 * 1000);
  adminInterval = setInterval(async () => adminStore.fetchStatus(), 3 * 60 * 1000);

  userStore.fetchUsers();
});

onUnmounted(() => {
  clearInterval(agentInterval);
  clearInterval(adminInterval);
});
</script>
